// Pure scoring and money-splitting rules. No I/O.

// Point multipliers for the metas; the roadmap page shows these same numbers.
export const MULTIPLIERS = { outbreak: 2, gold: 2, red: 3, bull: 2, bear: 2 };

// One Key nights (roadmap 46a): the doorkeeper holds one key for the shift. The one Open counts x2 either way, and once the key
// is spent Open becomes "To shelter", half a call: a neighbour sent to the shelter is safe there (+50), a visitor sent there gets
// in among people (-50). Both sides weigh the same, so a blind policy is still worth 0; the shift playtest picked x2 and 50 (78.7% at the least).
export const ONE_KEY = { mult: 2, away: 50 };

// The chalk streak (roadmap 28): +0.25x per tally, x2 at most, and a hard cap on any one door's multiplier.
export const STREAK = { step: 0.25, max: 2, cap: 6 };
// The streak a door pays after `tallies` in a row (the 5th door is the first at x2).
export const streakMult = tallies => Math.min(STREAK.max, 1 + STREAK.step * Math.max(0, tallies));
// One door's multiplier: the special times the streak, never above the cap; the outbreak x2 sits outside the cap.
export const doorMult = ({ special = 1, streak = 1, outbreak = false }) => (outbreak ? MULTIPLIERS.outbreak : 1) * Math.min(STREAK.cap, special * streak);

// What one finished door pays each side. correct: the doorkeeper's call was right (null: timeout); visitorScored: the knocker scored.
// dkTallies / vTallies: the streak counts going in, or null where there is no streak (guided lessons).
// Bull and bear replace the outbreak x2 and touch the doorkeeper only; the streak only multiplies points that are won.
export function doorMults({ special = null, correct, outbreak = false, visitorScored = false, dkTallies = null, vTallies = null, keyOpen = false }) {
  const beast = special === 'bull' || special === 'bear';
  const specialMult = special === 'red' ? MULTIPLIERS.red : special === 'gold' && correct ? MULTIPLIERS.gold : 1;
  // Roadmap 55c: the doorkeeper's streak and gold weigh a wrong call as much as a right one, so a blind guess stays worth nothing.
  const called = correct === true || correct === false;
  const dkStreak = dkTallies !== null && called ? streakMult(dkTallies) : 1;
  const vStreak = vTallies !== null && visitorScored ? streakMult(vTallies) : 1;
  const beastMult = special === 'bull' && correct ? MULTIPLIERS.bull : special === 'bear' && correct === false ? MULTIPLIERS.bear : 0;
  const mult = doorMult({ special: beast ? 1 : specialMult, streak: vStreak, outbreak: !beast && outbreak });
  const dkSpecial = Math.max(special === 'gold' && called ? MULTIPLIERS.gold : specialMult, keyOpen && called ? ONE_KEY.mult : 1);
  const dkMult = beastMult ? doorMult({ special: beastMult, streak: dkStreak }) : beast ? 1 : doorMult({ special: dkSpecial, streak: dkStreak, outbreak });
  return { mult, dkMult, dkStreak, vStreak };
}

// One draw x in [0, 1) picks at most one special: bands stack gold, red, bull, bear; a missing chance is off.
export function rollSpecial(x, { goldChance = 0, redChance = 0, bullChance = 0, bearChance = 0 } = {}) {
  let edge = goldChance;
  if (x < edge) return 'gold';
  if (x < (edge += redChance)) return 'red';
  if (x < (edge += bullChance)) return 'bull';
  if (x < (edge += bearChance)) return 'bear';
  return null;
}

// Roadmap 55c: a right call and a wrong call weigh the same, so a blind guess is worth nothing on average.
export const DOORKEEPER_POINTS = { right: 100, wrong: -100 };

// knockerReal: a player knocker scores only if they said something that reads as real text (see realtext.js).
export function scoreRound({ visitorKind, verdict, knockerReal = true }) {
  if (verdict === 'timeout') return { doorkeeper: 0, visitor: 0, shelterDamage: 0, correct: null };
  const isAlien = visitorKind === 'alien';
  if (verdict === 'away') return { doorkeeper: isAlien ? -ONE_KEY.away : ONE_KEY.away, visitor: 0, shelterDamage: 0, correct: !isAlien };
  const opened = verdict === 'open';
  const correct = isAlien ? !opened : opened;
  return {
    doorkeeper: correct ? DOORKEEPER_POINTS.right : DOORKEEPER_POINTS.wrong,
    // Human players are inspectors posing as aliens: they score when the doorkeeper wrongly keeps them out.
    visitor: visitorKind === 'human' && !opened && knockerReal ? 100 : 0,
    shelterDamage: isAlien && opened ? 1 : 0,
    correct,
  };
}

// A percentage may be fractional (12.5): worked in hundredths of a percent. Not a number: the fallback.
export const pctOf = (lamports, pct, fallback) => (lamports * BigInt(Math.round((Number.isFinite(pct) ? pct : fallback) * 100))) / 10000n;

export function feeSplit(lamports, { prizePct = 10, costsPct = 20 } = {}) {
  const prizes = pctOf(lamports, prizePct, 10);
  const costs = pctOf(lamports, costsPct, 20);
  return { prizes, costs, dev: lamports - prizes - costs };
}

// The rows a shift pays: wallets with points above 0, best first (ties keep the board's order), the top 10.
export const qualifiedRows = rows => rows.filter(r => r.wallet && r.points > 0).sort((a, b) => b.points - a.points);
export const payoutRows = rows => qualifiedRows(rows).slice(0, 10);

export function splitPrize(pool, rows) {
  const winners = rows
    .filter(r => r.points > 0)
    .sort((a, b) => b.points - a.points)
    .slice(0, 10);
  if (winners.length === 0) return [];
  const total = BigInt(winners.reduce((s, r) => s + r.points, 0));
  const out = winners.map(r => ({ playerId: r.playerId, wallet: r.wallet, lamports: (pool * BigInt(r.points)) / total }));
  const paid = out.reduce((s, o) => s + o.lamports, 0n);
  out[0].lamports += pool - paid;
  return out;
}
